import { ensureHtmlElement } from "@nectari/utils/html";
import NecIcon from "../../shared/necIcon";
import { AuthType } from "./types";
import { hideSpinner, showSpinner } from "./utils";

const currentAuthTypeAttribute = "data-current-auth-type";
const authTypeAttribute = "data-auth-type";
const authProviderIdAttribute = "data-provider-id";

/**
 * DOM selectors for elements shared between the LoginForm and ResetPasswordForm.
 */
class UserForm {
    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        this.element = form;
    }

    get container() {
        return ensureHtmlElement(this.element.querySelector(".container"));
    }

    get domainInput() {
        return ensureHtmlElement(this.element.querySelector("input[name='domain']"), HTMLInputElement);
    }

    get usernameInput() {
        return ensureHtmlElement(this.element.querySelector("input[name='username']"), HTMLInputElement);
    }

    get centralpointSelect() {
        return ensureHtmlElement(this.element.querySelector(".formInput.centralpointSelect"), HTMLDivElement);
    }

    get centralpointInput() {
        return ensureHtmlElement(this.centralpointSelect.querySelector("input"), HTMLInputElement);
    }

    get card() {
        return ensureHtmlElement(this.element.querySelector("div.card"), HTMLDivElement);
    }

    get submitButton() {
        return ensureHtmlElement(this.element.querySelector(".formActions input[type='submit']"), HTMLInputElement)
    }

    get errorMessage() {
        return ensureHtmlElement(this.element.querySelector(".errorMessage"), HTMLSpanElement)
    }

    get formToggle() {
        return ensureHtmlElement(this.element.querySelector("a.formToggle"), HTMLAnchorElement);
    }

    get isActive() {
        return this.element.classList.contains("active");
    }

    getFormData() {
        return new FormData(this.element);
    }

    /**
     * @param {string} message
     */
    showErrorMessage(message) {
        this.errorMessage.innerText = message;
        this.errorMessage.style.visibility = "visible";
    }

    clearErrorMessage() {
        this.errorMessage.innerText = "";
        this.errorMessage.style.visibility = "hidden";
    }

    /**
     * @param {boolean} [force]
     */
    toggleSubmitButton(force) {
        this.submitButton.disabled = force === undefined
            ? !this.submitButton.disabled
            : !force;
    }

    toggleActive() {
        this.element.classList.toggle("active");
    }

    /**
     * @param {string} [message]
     * @param {string} [customMessageClass]
     */
    displayLoaders(message, customMessageClass) {
        showSpinner(this.card, message, customMessageClass);
    }

    hideLoaders() {
        hideSpinner(this.card);
    }
}

/**
 * DOM Selectors for elements in the LoginForm.
 */
class LoginForm extends UserForm {
    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        super(form)
    }

    /**
     * @param {boolean} [force]
     */
    toggleServerError(force) {
        this.element.classList.toggle("serverError", force);
    }

    toggleAuthType() {
        if (this.#authType !== AuthType.both) {
            // This shouldn't be possible. 
            // If only one one auth type is enabled, the toggle shouldn't be available.

            this.#currentAuthType = this.#authType;
        }
        else {
            this.#currentAuthType = this.#currentAuthType === AuthType.basic
                ? AuthType.external
                : AuthType.basic;
        }
    }

    /**
     * @param {import("./types").AuthService[]} providers 
     */
    setAuthProviders(providers) {
        this.element.querySelectorAll(".authProvider").forEach(element => element.remove());

        this.externalProviders.append(...providers.map(provider => {
            const clone = ensureHtmlElement(this.#authProviderTemplate.content.cloneNode(true), DocumentFragment);
            const button = ensureHtmlElement(clone.querySelector("button"), HTMLButtonElement);

            button.setAttribute(authProviderIdAttribute, provider.id);
            button.innerText = provider.description;

            return button;
        }));
    }

    /**
     * @param {AuthType} authType
     * @param {AuthType} currentAuthType
     */
    setAuthAttributes(authType, currentAuthType) {
        this.#authType = authType;
        this.#currentAuthType = currentAuthType;
    }

    get screenTypeToggle() {
        return ensureHtmlElement(this.element.querySelector(".imageInput.screenTypeToggle"), HTMLSpanElement);
    }

    get authTypeToggles() {
        return ensureHtmlElement(this.element.querySelectorAll("a.authTypeToggle"), NodeList);
    }

    get externalProviders() {
        return ensureHtmlElement(this.element.querySelector(".externalProviders"), HTMLDivElement)
    }

    get reloadLoginOptions() {
        return ensureHtmlElement(this.element.querySelector("#reloadLoginOptions"), HTMLButtonElement);
    }

    get closeAppButton() {
        const button = this.element.querySelector("nec-icon.closeApp");

        return button ? ensureHtmlElement(button, NecIcon) : undefined;
    }

    /**
     * @param {*} value 
     * @returns {AuthType}
     */
    #authTypeGuard(value) {
        if (typeof value !== "string" || !(value in AuthType)) {
            throw new Error("Invalid auth type");
        }

        switch (value) {
            case AuthType.both:
                return AuthType.both;
            case AuthType.external:
                return AuthType.external;
            case AuthType.basic:
                return AuthType.basic;
            case AuthType.none:
            default:
                return AuthType.none;
        }
    }

    get #authProviderTemplate() {
        return ensureHtmlElement(this.element.querySelector("#authProviderTemplate"), HTMLTemplateElement);
    }

    /**
     * @param {AuthType} value
     */
    set #authType(value) {
        this.element.setAttribute(authTypeAttribute, value);
    }

    get #authType() {
        return this.#authTypeGuard(this.element.getAttribute(authTypeAttribute));
    }

    /**
     * @param {AuthType} value
     */
    set #currentAuthType(value) {
        this.element.setAttribute(currentAuthTypeAttribute, value);
    }

    get #currentAuthType() {
        return this.#authTypeGuard(this.element.getAttribute(currentAuthTypeAttribute));
    }

    static create() {
        return new LoginForm(ensureHtmlElement(document.getElementById("loginForm"), HTMLFormElement));
    }
}

/**
 * DOM Selectors for elements in the ResetPasswordForm.
 */
class ResetPasswordForm extends UserForm {
    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        super(form)
    }

    static create() {
        return new ResetPasswordForm(ensureHtmlElement(document.getElementById("resetPasswordForm"), HTMLFormElement));
    }
}

/**
 * DOM Selectors for elements in the SuccessForm.
 */
class SuccessForm {
    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        this.element = form;
    }

    /**
     * @param {boolean} value
     */
    setMobileMode(value) {
        this.#m.value = `${value}`;
    }

    /**
     * @param {string} value
     */
    setCentralpoint(value) {
        this.#centralpoint.value = value;
    }

    /**
     * @param {string} value
     */
    setLanguage(value) {
        this.#language.value = value;
    }

    get #m() {
        return ensureHtmlElement(this.element.querySelector("input[name='M']"), HTMLInputElement);
    }

    get #n() {
        try {
            return ensureHtmlElement(this.element.querySelector("input[name='N']"), HTMLInputElement);
        }
        catch {
            return;
        }
    }

    get #centralpoint() {
        return ensureHtmlElement(this.element.querySelector("input[name='CP']"), HTMLInputElement);
    }

    get #language() {
        return ensureHtmlElement(this.element.querySelector("input[name='Lang']"), HTMLInputElement);
    }

    get returnUrl() {
        return ensureHtmlElement(this.element.querySelector("input[name='returnUrl']"), HTMLInputElement);
    }

    static create() {
        return new SuccessForm(ensureHtmlElement(document.getElementById("successForm"), HTMLFormElement));
    }
}

export { UserForm, LoginForm, ResetPasswordForm, SuccessForm, authProviderIdAttribute };